import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useTranslation } from "react-i18next";
import useDialog from "../../../utils/hooks/useDialog";
import { testIds } from "./ReportActions.testIds";

export interface SavedReportOption {
  _id: string;
  name: string;
}

interface ReportActionsProps {
  savedReports: SavedReportOption[];
  /** Currently loaded saved report id, if any. */
  selectedSavedReportId: string | null;
  /** True when there is captured filter state available to save. */
  canSave: boolean;
  saving: boolean;
  onSave: (name: string) => void;
  onSelectSavedReport: (id: string) => void;
  onClearSavedReport: () => void;
  onDeleteSavedReport: (id: string) => void;
}

export default function ReportActions({
  savedReports,
  selectedSavedReportId,
  canSave,
  saving,
  onSave,
  onSelectSavedReport,
  onClearSavedReport,
  onDeleteSavedReport,
}: ReportActionsProps) {
  const { t } = useTranslation("reporting");
  const { showDialog, hideDialog } = useDialog();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");

  const confirmDelete = (id: string, reportName: string) => {
    showDialog({
      type: "error",
      title: t("savedReports.deleteTitle", "Delete saved report"),
      description: t("savedReports.deleteConfirm", {
        name: reportName,
        defaultValue: `Delete "${reportName}"? This can't be undone.`,
      }),
      actions: [
        { title: t("savedReports.cancel", "Cancel"), type: "secondary" },
        {
          title: t("savedReports.delete", "Delete"),
          type: "error",
          onClick: () => {
            hideDialog();
            onDeleteSavedReport(id);
          },
        },
      ],
    });
  };

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave(trimmed);
    setName("");
    setDialogOpen(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        width: "100%",
        px: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <TextField
          select
          size="small"
          label={t("savedReports.select", "Saved reports")}
          value={selectedSavedReportId ?? ""}
          inputProps={{ "data-testid": testIds.reportActionsSavedReports } as React.InputHTMLAttributes<HTMLInputElement>}
          onChange={(e) => onSelectSavedReport(e.target.value)}
          sx={{ minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <BookmarkBorderIcon fontSize="small" sx={{ mr: 1 }} />
            ),
            endAdornment: selectedSavedReportId ? (
              <IconButton data-testid={testIds.reportActionsClear}
                size="small"
                aria-label={t("savedReports.clear", "Clear")}
                onClick={onClearSavedReport}
                sx={{ mr: 2 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : null,
          }}
        >
          {savedReports.length === 0 ? (
            <MenuItem data-testid={testIds.reportActionsNoSavedReports} value="" disabled>
              {t("savedReports.empty", "No saved reports")}
            </MenuItem>
          ) : (
            savedReports.map((r) => (
              <MenuItem data-testid={`${testIds.reportActionsSavedReportOption}-${r._id}`}
                key={r._id}
                value={r._id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                {r.name}
                <IconButton data-testid={`${testIds.reportActionsDeleteSavedReport}-${r._id}`}
                  size="small"
                  edge="end"
                  aria-label={t("savedReports.delete", "Delete")}
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmDelete(r._id, r.name);
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </MenuItem>
            ))
          )}
        </TextField>

        <Button data-testid={testIds.reportActionsOpenSaveDialog}
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          disabled={!canSave || saving || !!selectedSavedReportId}
          onClick={() => setDialogOpen(true)}
        >
          {t("savedReports.save", "Save")}
        </Button>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{t("savedReports.saveTitle", "Save report")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={t("savedReports.nameLabel", "Report name")}
            value={name}
            inputProps={{ "data-testid": testIds.reportActionsSavedReportName } as React.InputHTMLAttributes<HTMLInputElement>}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button data-testid={testIds.reportActionsCancel} onClick={() => setDialogOpen(false)}>
            {t("savedReports.cancel", "Cancel")}
          </Button>
          <Button data-testid={testIds.reportActionsSave}
            variant="contained"
            onClick={handleSave}
            disabled={!name.trim() || saving}
          >
            {t("savedReports.save", "Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
